import { EFinancingProgram } from '@if/core/types'
import { ControlledTextField, type FormDialogContentContext } from '@if/mui'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material'
import { useAgentWithdrawalReasons } from '@src/data/api/agent-withdrawal-reason/agent-withdrawal-reason-api'
import { AgentWithdrawalReasonItem } from '@src/data/types'
import { useEffect, useMemo, useState } from 'react'
import { useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  type AgentWithdrawalReasonFormValues,
  MAXIMUM_OTHER_COMMENT_LENGTH,
  OTHER_REASON_ID,
} from '../agentWithdrawalReasonForm'

export interface AgentWithdrawalReasonOpenProps {
  financingProgramId: EFinancingProgram
  creditApplicationId: string
}

type AgentWithdrawalReasonCtx = FormDialogContentContext<
  AgentWithdrawalReasonFormValues,
  AgentWithdrawalReasonOpenProps,
  void
>

const AgentWithdrawalReasonDialogContent = ({ ctx }: { ctx: AgentWithdrawalReasonCtx }) => {
  const { t, i18n } = useTranslation()
  const { data: reasonList } = useAgentWithdrawalReasons()
  const { control, getValues, setValue } = ctx.form

  const selectedReasonId = useWatch({ control, name: 'reasonId' })
  const [expandedCategory, setExpandedCategory] = useState<string | false>(false)

  const categories = useMemo(() => {
    const activeReasons = (reasonList?.items ?? [])
      .filter((reason) => reason.active)
      .sort((one, two) => (one.priority ?? 0) - (two.priority ?? 0))

    return activeReasons.reduce<Record<string, AgentWithdrawalReasonItem[]>>((grouped, reason) => {
      grouped[reason.category] = [...(grouped[reason.category] ?? []), reason]
      return grouped
    }, {})
  }, [reasonList])

  // Keyed on the list alone: re-running on every pick would fight the agent's own expanding.
  useEffect(() => {
    const savedCategory = reasonList?.items.find((reason) => reason.value === getValues('reasonId'))?.category
    if (savedCategory) setExpandedCategory(savedCategory)
  }, [reasonList, getValues])

  const handleCategoryChange = (category: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedCategory(isExpanded ? category : false)
  }

  return (
    <Grid container spacing={1}>
      {Object.entries(categories).map(([category, reasons]) => (
        <Grid key={category} size={{ xs: 12, sm: 6 }}>
          <Accordion expanded={expandedCategory === category} onChange={handleCategoryChange(category)} disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>{t(`agentWithdrawalReason.category.${category}`, { defaultValue: category })}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <RadioGroup
                value={selectedReasonId}
                onChange={(event) =>
                  setValue('reasonId', event.target.value, { shouldDirty: true, shouldValidate: true })
                }
              >
                {reasons.map((reason) => (
                  <FormControlLabel
                    key={reason.value}
                    value={reason.value}
                    control={<Radio />}
                    label={i18n.language === 'fr' ? reason.textFr : reason.textEn}
                  />
                ))}
              </RadioGroup>
            </AccordionDetails>
          </Accordion>
        </Grid>
      ))}

      {selectedReasonId === OTHER_REASON_ID && (
        <Grid size={12}>
          <ControlledTextField
            control={control}
            name="comment"
            label={t('agentWithdrawalReason.commentLabel')}
            fullWidth
            multiline
            minRows={2}
            slotProps={{ htmlInput: { maxLength: MAXIMUM_OTHER_COMMENT_LENGTH } }}
          />
        </Grid>
      )}
    </Grid>
  )
}

export default AgentWithdrawalReasonDialogContent
